import { DropdownButton, DropdownButtonType } from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';
import { classes } from 'typestyle';
import _ from 'underscore';

import { HelpService } from '~commonInfra/services/HelpService';
import { Telemetry } from '~commonInfra/services/Telemetry';
import { PageHeaderButtonClickEvent, TelemetryEvents } from '~commonInfra/telemetryEvents/TelemetryEvents';
import { MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import {
  CompositeSessionInfoSelectors,
  SessionServerSelectors
} from '~src/bootstrap/redux/selectors/SessionInfoSelectors';

import { AppState } from '../../../bootstrap/redux/state/AppState';
import { Messages } from '../../../codegen/Localize';
import {
  getSessionManagementServiceInstance,
} from '../../../sessionManagement/SessionManagementService';
import { ServerSettingsUnauthenticated } from '../../../settings/services/ServerSettingsUnauthenticated';
import { HelpActions } from '../../actions/HelpActions';
import { PageHeaderButtonType } from '../layout/PageHeader';
import { PageHeaderStyles } from '../layout/PageHeaderStyles';
import { HeaderIcons } from './HeaderIcons';
import { HelpMenuPanel, HelpMenuPanelProps } from './HelpMenuPanel';
import backgroundHover = PageHeaderStyles.backgroundHover;
import cssVar = PageHeaderStyles.CssVariables.cssVar;
import CssVariables = PageHeaderStyles.CssVariables;

export interface HelpMenuExternalProps {
  readonly style?: any;
  readonly buttonType: PageHeaderButtonType;
}

export interface HelpMenuPropsFromRedux {
  readonly isOfflineHelpEnabled: boolean;
  readonly isSessionSiteOrServerAdmin: boolean;
}

export interface HelpMenuPropsFromServices {
  readonly helpActions: HelpActions;
  readonly serverHelpLabel: string;
  readonly serverHelpUrl: string;
  readonly serverName: string;
  readonly supportUrl: string;
  readonly whatIsNewUrl: string;
  readonly systemStatusUrl: string;
  readonly serverEnvironment: string;
}

export interface HelpMenuViewComponentState {
  buttonHover: boolean;
  panelOpened: boolean;
}

export type HelpMenuViewComponentProps = HelpMenuExternalProps & HelpMenuPropsFromRedux & HelpMenuPropsFromServices;

export class HelpMenuViewComponent extends React.Component<HelpMenuViewComponentProps, HelpMenuViewComponentState> {
  private dropdownButtonRef: React.RefObject<DropdownButton> = React.createRef();
  private helpMenuPanelProps: HelpMenuPanelProps;
  private unmounted: boolean = false;

  constructor(props: HelpMenuViewComponentProps) {
    super(props);
    this.helpMenuPanelProps = {
      ...this.props,
      itemSelected: this.closePanel
    };
    this.state = {buttonHover: false, panelOpened: false};
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public render(): JSX.Element {
    const buttonClasses = classes(
      PageHeaderStyles.headerDropdownButton,
      backgroundHover
    );

    return (
      <div onMouseEnter={this.onMouseEnter} onMouseLeave={this.onMouseLeave}>
      <DropdownButton
        aria-controls={'help-menu-panel'}
        aria-haspopup={'menu'}
        aria-label={Messages.helpAltText()}
        closeOnPopupBlur={true}
        focusOnClose={true}
        ref={this.dropdownButtonRef}
        label={undefined}
        testId={'help-menu'}
        type={DropdownButtonType.Icon}
        children={this.getIconButtonContent()}
        popupContent={this.dropdownContent()}
        className={buttonClasses}
        title={Messages.helpTitle()}
        onOpen={this.onDropdownOpen}
        onClose={this.closePanel}
        popupAlignment={'right'}
      />
      </div>
    );
  }

  private onMouseEnter = () => {
    this.updateState({buttonHover: true});
  };

  private onMouseLeave = () => {
    this.updateState({buttonHover: false});
  };

  private dropdownContent = () => {
    this.helpMenuPanelProps = _.extend(this.helpMenuPanelProps, this.props);
    return <HelpMenuPanel {...this.helpMenuPanelProps}/>;
  };

  private onDropdownOpen = () => {
    Telemetry.sendEvent<PageHeaderButtonClickEvent>({
      type: TelemetryEvents.PageHeaderButtonClick,
      button: 'help-menu',
      action: 'open'
    });
    this.updateState({panelOpened: true});
  };

  private closePanel = () => {
    if (this.dropdownButtonRef.current) {
      this.dropdownButtonRef.current.toggle(false);
      this.updateState({panelOpened: false});
    }
  };

  private getIconButtonContent = () => {
    const active = this.state.buttonHover || this.state.panelOpened;
    const newHeader = this.props.buttonType === 'short' ?
      HeaderIcons.HelpIcon(active) :
      HeaderIcons.LargeHelpIcon(active);

    return <span draggable={false}
    style={{
      width: cssVar(CssVariables.headerIconSize),
      height: cssVar(CssVariables.headerIconSize),
      backgroundSize: 'unset',
      backgroundImage: `url(${newHeader})`
    }}/>;
  };

  private updateState<K extends keyof HelpMenuViewComponentState>(newState: Pick<HelpMenuViewComponentState, K>): void {
    if (!this.unmounted) {
      this.setState(newState);
    }
  }
}

export namespace ReduxConnectedHelpMenu {

  const mapStateToProps: MapStateToProps<HelpMenuPropsFromRedux> = (state: AppState) => ({
    isOfflineHelpEnabled: SessionServerSelectors.offlineHelpEnabled(state),
    isSessionSiteOrServerAdmin: CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector(state),
  });

  export const HOC = connect(mapStateToProps);
}


/*
 * Three levels of higher-order provider components, to gather props from three different sources:
 *   - Connected Redux component (props from Redux)
 *   - ServiceProvider (props from services)
 *   - Async store HOC (props from other API calls)
 */
export const HelpMenu: React.ComponentType<HelpMenuExternalProps> =
  ReduxConnectedHelpMenu.HOC(
    function ServicesProvider(props: HelpMenuExternalProps & HelpMenuPropsFromRedux) {
      const sessionManagementService = getSessionManagementServiceInstance();
      const helpActions = new HelpActions(sessionManagementService);
      return (
        <HelpMenuViewComponent
          {...props}
          helpActions={helpActions}
          serverHelpLabel={Messages.tableauHelp()}
          serverHelpUrl={HelpService.getServerHelpUrlByCategory()}
          supportUrl={HelpService.getGeneralSupportUrl()}
          whatIsNewUrl={HelpService.supportUrlPrefix() + '/support/releases/online'}
          systemStatusUrl={HelpService.getSystemStatusUrl()}
          serverName={ServerSettingsUnauthenticated.serverName()}
          serverEnvironment={sessionManagementService.helpEdition()}
        />);
    }
  );
